import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, Snackbar, Alert, useMediaQuery } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

// Updated imports

//import { setOpportunityDetails } from '../../../../slices/ideationSlice/newIdeationSlice';

import { FileViewer } from '../../../NewSolutionXPLR/modules/GeneralInformation';

// Import local types, constants, hooks, components
import { ChatSession } from '../../types';
import { useDraftManagement } from '../../hooks/useDraftManagement';
import { useFileUpload } from '../../hooks/useFileUpload';
import FileUploadSection from './FileUploadSection';
import InputSection from './InputSection';
import { setOpportunityDetails } from '../../../NewIdeationXPLR/state';

interface ProcessCreationProps {
  // handleBack: () => void;
  field: string;
  handleForward: (id: string) => void;
  sessions?: ChatSession[];
  currentChatId?: string;
  onSelectChat?: (chatId: string) => void;
  onNewChat?: () => void;
  onNewIdeation?: () => void;
  onFieldChange?: (field: string) => void;
}

const ProcessCreation: React.FC<ProcessCreationProps> = ({
  // handleBack,
  field,
  handleForward,
  currentChatId: propCurrentChatId,
  onFieldChange,
}) => {
  const dispatch = useDispatch();
  const [filePreview, setFilePreview] = useState<any>(null);
  const organization_id = localStorage.getItem('current_organization') || '';

  const [currentChatId, setCurrentChatId] = useState<string>('');

  // Navigation state - similar to KnowledgeCreation
  const [_useCaseId, setUseCaseId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = useMediaQuery('(max-width:900px)');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'error',
  });

  const showSnackbar = (message: string, severity: 'error' | 'success' | 'warning' = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Draft Management Hook
  const {
    details,
    setDetails,
    uploadedFiles,
    setUploadedFiles,
    selectValue,
    setSelectValue,
    clearDraftFromStorage,
  } = useDraftManagement({
    currentChatId: propCurrentChatId,
    field,
    onFieldChange,
  });

  // File Upload Hook
  const { fileInputRef, handleAddFile, handleFileUpload, handleRemoveFile, handlePaste } =
    useFileUpload({
      setUploadedFiles,
      details,
      setDetails,
      showSnackbar,
      isSubmitting,
    });

  // Sync propCurrentChatId
  useEffect(() => {
    if (propCurrentChatId && propCurrentChatId !== currentChatId) {
      setCurrentChatId(propCurrentChatId);
    }
  }, [propCurrentChatId]);

  useEffect(() => {
    const shouldSendContext = sessionStorage.getItem('sendContextForField');
    const contextFieldValue = sessionStorage.getItem('contextFieldValue');

    if (shouldSendContext && field === shouldSendContext && contextFieldValue) {
      const plainText = stripHtmlTags(contextFieldValue);
      setDetails(plainText);

      sessionStorage.removeItem('sendContextForField');
      sessionStorage.removeItem('contextFieldValue');
    }
  }, [field, setDetails]);

  const handleDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDetails(event.target.value);
  };

  const isForwardEnabled = () => {
    if (details.trim() || uploadedFiles.length) {
      if (uploadedFiles.some((f) => f.isUploading || f.isAnalyzing || f.failed)) {
        return false;
      }
      return true;
    }
    return false;
  };

  const handleSubmit = async (_useCaseId: string = currentChatId || '') => {
    if (!isForwardEnabled() || isSubmitting) {
      return;
    }

    if (!organization_id) {
      showSnackbar('Organization ID is missing. Please login again.', 'error');
      console.error('Organization ID is empty');
      return;
    }

    setIsSubmitting(true);

    try {
      const generatedUuid = uuidv4();

      handleForward(generatedUuid);

      showSnackbar('Query submitted successfully!', 'success');

      dispatch(
        setOpportunityDetails({
          uploadedFiles: uploadedFiles,
          detailsText: details,
          finalSummary: '', // was state.fileSummary, but it wasn't really used/updated in original code except initial empty state
        })
      );

      setUseCaseId(generatedUuid);
      clearDraftFromStorage();
    } catch (error) {
      console.error('Error submitting query:', error);
      showSnackbar('An error occurred. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Attached contexts logic (was updating local state 'attachedContexts', but that state wasn't used in render??)
  // Let's check original render.
  // In original code, 'attachedContexts' was in 'state', but NOT used in the JSX.
  // So I can probably remove it.
  // Wait, I should double check if 'attachedContexts' was used.
  // searching original code for 'attachedContexts'...
  // It was set in useEffect (lines 640-665).
  // But searching usage... line 123: interface property.
  // line 226: initial state.
  // It doesn't seem to be used in the return JSX!
  // So I can safely remove it.

  return (
    <Box
      sx={{
        display: 'flex',
        height: isMobile ? 'auto' : '100vh',
        minHeight: isMobile ? 0 : undefined,
        backgroundColor: '#ffffff',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: isMobile ? 'visible' : 'hidden',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              cursor: 'pointer',
            }}
          ></Box>
        </Box>

        {/* Main Content - Centered */}
        <Box
          sx={{
            flex: isMobile ? 'unset' : 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',

            px: { xs: 2, md: 4 },
            // Vertical breathing room on mobile
            pt: { xs: 4, md: 0 },
            pb: { xs: 4, md: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '85%' },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: { xs: '24px', md: '128px' },
            }}
          >
            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#131426',
                fontSize: { xs: '20px', md: '28px' },
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              Create Knowledge Using Data XPLR
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontFamily: 'Poppins',
                color: '#131426',
                mb: { xs: 3, md: 4 },
                fontSize: '15px',
                lineHeight: 1.5,
              }}
            >
              Add instructions or files to generate semantic knowledge for selected category
            </Typography>

            {/* Input Box */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', sm: '90%' },
                  maxWidth: '1078px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '12px',
                  padding: { xs: '14px', sm: '16px' },
                  mx: { xs: 0, sm: 'auto' },
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  '&:focus-within': {
                    borderColor: '#B9B9B9',
                  },
                  boxSizing: 'border-box',
                }}
              >
                <FileUploadSection
                  uploadedFiles={uploadedFiles}
                  onFileRemove={handleRemoveFile}
                  onFilePreview={(url) => setFilePreview(url)}
                  isSubmitting={isSubmitting}
                />
                <InputSection
                  details={details}
                  onDetailsChange={handleDetailsChange}
                  onPaste={handlePaste}
                  isSubmitting={isSubmitting}
                  selectValue={selectValue}
                  onSelectChange={(val) => {
                    setSelectValue(val);
                    onFieldChange?.(val);
                  }}
                  onAddFile={handleAddFile}
                  onSubmit={handleSubmit}
                  isForwardEnabled={isForwardEnabled()}
                  fileInputRef={fileInputRef}
                  onFileUpload={handleFileUpload}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* File Viewer Modal */}
      {filePreview && (
        <FileViewer
          fileUrl={filePreview}
          onClose={() => setFilePreview(null)}
          fileName={uploadedFiles.find((f) => f.url === filePreview)?.name || ''}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessCreation;
